/* @flow */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import glamorous from 'glamorous';
import Helmet from 'react-helmet';
import Page from 'app/containers/parts/Page';
import t from 'app/utils/translate';
import colorUtils from 'color';
import GroupedChallengesList from 'app/components/challenges/GroupedChallengesList';
import trainerChallengesSelector from 'app/selectors/trainerChallengesSelector';
import SportsFilterBox from 'app/components/sportsFilters/SportsFilterBox';
import LightTabSet from 'app/components/commons/LightTabSet';
import LightTab from 'app/components/commons/LightTab';
import { COMPLETED, LIVE, UPCOMING } from 'app/constants/tabTypes';
import { startTrainerChallengesPoll, stopTrainerChallengessPoll } from 'app/actions/trainerActions';
import { setLobbySport, setLobbySubCategory } from 'app/actions/lobbyActions';

type Props = {
  activeSport?: string,
  activeSubCategory?: string,
  challengesData: Array<ChallengeData>,
  isFetching?: boolean,
  isSignedIn?: boolean,
  onLoadChallenges: () => void,
  onSportSelect: (sport: string, subCategory?: string) => void,
  onSubCategorySelect: (subCategory: string) => void,
  onStopPoll: () => void,
};

type State = {
  activeTab: COMPLETED | LIVE | UPCOMING,
};

const tabs = [
  { name: t('Upcoming'), id: UPCOMING, selected: false },
  { name: t('Live'), id: LIVE, selected: false },
  { name: t('Completed'), id: COMPLETED, selected: false },
];

const TrainerPage = glamorous(Page)({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto',
});

const TopMessage = glamorous.div(({ theme }) => ({
  height: 70,
  textTransform: 'uppercase',
  backgroundColor: colorUtils(theme.color.panel)
    .alpha(0.6)
    .toString(),
  color: theme.color.white,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '10px 10px 0 0',
  textAlign: 'center',
  paddingLeft: theme.spacing.small,
  paddingRight: theme.spacing.small,
}));

class Trainer extends Component<Props, State> {
  props: Props;

  state: State = {
    activeTab: UPCOMING,
  };

  static defaultProps = {
    activeSport: undefined,
    activeSubCategory: undefined,
    isFetching: false,
    isSignedIn: false,
  };

  componentDidMount() {
    const { onLoadChallenges } = this.props;

    onLoadChallenges();
  }

  componentDidUpdate(prevProps: Props) {
    const { isSignedIn, onLoadChallenges } = this.props;

    if (prevProps.isSignedIn && !isSignedIn) {
      onLoadChallenges();
    }
  }

  componentWillUnmount() {
    const { onStopPoll } = this.props;

    onStopPoll();
  }

  handleSelectTab = (activeTab) => {
    this.setState({ activeTab });
  };

  render() {
    const {
      activeSport,
      activeSubCategory,
      challengesData,
      isFetching,
      onSportSelect,
      onSubCategorySelect,
    } = this.props;
    const { activeTab } = this.state;

    return (
      <TrainerPage title={t('Training Ground')} showAd>
        <Helmet title={t('Training Ground')} />
        <TopMessage>
          {t('Training Ground is an accelerated practice facility, designed to simulate the live experience.')}
        </TopMessage>
        <SportsFilterBox
          activeSport={activeSport}
          activeSubCategory={activeSubCategory}
          namespace="/training"
          onSportSelect={onSportSelect}
          onSubCategorySelect={onSubCategorySelect}
          showAll
          showSubCategories
        />
        <LightTabSet selectedIndex={tabs.findIndex(tab => activeTab === tab.id)}>
          {tabs.map(tab => (
            <LightTab
              key={tab.name}
              id={tab.id}
              onSelect={this.handleSelectTab}
              selected={activeTab === tab.id}
            >
              {tab.name}
            </LightTab>
          ))}
        </LightTabSet>
        <GroupedChallengesList
          challengesData={challengesData.filter(g => g.id === activeTab)}
          isFetching={isFetching}
        />
      </TrainerPage>
    );
  }
}

function mapStateToProps(state: GlobalState) {
  return {
    activeSport: state.lobbyChallenges.activeSport,
    activeSubCategory: state.lobbyChallenges.activeSubCategory,
    challengesData: trainerChallengesSelector(state),
    isFetching: state.trainerChallenges.isFetching,
    isSignedIn: state.user.isSignedIn,
  };
}

function mapDispatchToProps(dispatch: Dispatch<*>) {
  return {
    onSportSelect(sport: string, subCategory?: string) {
      dispatch(setLobbySport(sport, subCategory));
    },
    onSubCategorySelect(sport: string) {
      dispatch(setLobbySubCategory(sport));
    },
    onLoadChallenges() {
      dispatch(startTrainerChallengesPoll(true));
    },
    onStopPoll() {
      dispatch(stopTrainerChallengessPoll());
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Trainer);
